import React from 'react';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import AaroLoadingScreen from 'components/Loading';
import pxToRem from 'assets/theme/functions/pxToRem.js';
import Dialog from '@mui/material/Dialog';

import { notify } from 'helpers/notificationHelper';
import { useNavigate } from 'react-router-dom';

import { getAllTagGroups, updateTagGroup } from 'services/api/general/tagGroup.js';
import AddTagGroup from './AddTagGroup';

const fetchAllTagGroups = async () => {
    const response = await getAllTagGroups().then((rp) => (rp.data?.result ? rp.data?.result : []));
    return response;
};

export default function EditTagGroups() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [openAddDialog, setOpenAddDialog] = React.useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ['allTagGroups'],
        queryFn: () => fetchAllTagGroups(),
        staleTime: 60000,
    });

    const handleRowEditCommit = React.useCallback((params) => {
        const id = params.id;
        const key = params.field;
        const value = params.value;
        let obj = {};
        obj[key] = value;
        updateCell(id, obj);
    }, []);

    // * Edit cells section
    const updateCell = React.useCallback(
        (id, obj) => {
            updateTagGroup(id, obj)
                .then((rp) => {
                    notify(rp.data.success, rp.data.message);
                    queryClient.invalidateQueries({ queryKey: ['allTagGroups'] });
                })
                .catch((err) => {
                    notify(false, err.message);
                });
        },
        [queryClient]
    );

    if (isLoading) return <AaroLoadingScreen />;
    if (error) return 'Bir Hata Oluştu: ' + error.message;

    const columns = [
        {
            field: '_id',
            headerName: 'ID',
            flex: 1,
            editable: false,
            hide: true, // Hidden by default
        },
        {
            field: 'name',
            headerName: 'Tag Grup Adı',
            flex: 1,
            editable: true,
        },
        {
            field: 'description',
            headerName: 'Açıklama',
            flex: 1,
            editable: true,
        },
        {
            field: 'tagsCount',
            headerName: 'Tag Sayısı',
            flex: 0.5,
            renderCell: (params) => {
                return <MDTypography variant="body2">{params.row.tags?.length || 0}</MDTypography>;
            },
        },
        {
            field: 'viewColumn',
            headerName: 'Etiketleri Görüntüle',
            flex: 0.8,
            renderCell: (params) => (
                <MDButton
                    variant="outlined"
                    size="small"
                    color="info"
                    onClick={() => {
                        navigate(`/settings?tagGroup=${params.row.id}`);
                    }}
                >
                    Etiketleri Görüntüle
                </MDButton>
            ),
        },
    ];

    const rows = data.map((group) => ({
        id: group._id,
        _id: group._id, // Add the ID as a separate field
        name: group.name,
        description: group.description,
        tags: group.tags,
    }));

    return (
        <>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6">Tag Grupları</MDTypography>
                <MDButton color="success" onClick={() => setOpenAddDialog(true)}>
                    Yeni Tag Grubu Ekle
                </MDButton>
            </MDBox>
            <MDBox color="white" bgColor="white" variant="gradient" borderRadius="lg" shadow="lg" opacity={1} p={2}>
                <DataGridPro
                    autoHeight
                    autoPageSize
                    components={{
                        Toolbar: GridToolbar,
                    }}
                    onCellEditCommit={handleRowEditCommit}
                    density="compact"
                    disableSelectionOnClick
                    rows={rows}
                    columns={columns}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                _id: false, // Hide ID column by default
                            },
                        },
                    }}
                    sx={{
                        fontSize: pxToRem(12),
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                        fontWeight: 400,
                        lineHeight: 1.25,
                    }}
                />
            </MDBox>

            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
                <AddTagGroup setOpenDialog={setOpenAddDialog} />
            </Dialog>
        </>
    );
}
